import { CsvItineraryRow, Itinerary, ItinerarySummary, UpdatedFieldCounts } from '../types/itinerary';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { debug } from './debug';

export const NOTIFICATION_TRIGGER_FIELDS: ReadonlyArray<keyof UpdatedFieldCounts> = [
  'cruiseLength',
  'departurePort',
  'portNames',
  'portDates',
  'portTimes',
  'portsCount'
];

export interface ItineraryChangeDetails {
  changedFields: Array<keyof UpdatedFieldCounts>;
  shouldNotify: boolean;
  changeDescription: string;
}

export interface ComparisonResult {
  newItineraries: CsvItineraryRow[];
  updatedItineraries: CsvItineraryRow[];
  unchangedItineraries: CsvItineraryRow[];
  summariesToUpdate: Map<string, ItinerarySummary>; // Key: cruiseLine_shipName
  oldDocIdsToDelete: string[]; // Legacy docs to delete after migration
  updatedFieldCounts?: UpdatedFieldCounts;
  itineraryChanges: Map<string, ItineraryChangeDetails>; // Key: newDocId
}

/** New docId format: includes returnDate for same-date different-length cruises */
export function getItineraryDocId(cruiseLine: string, shipName: string, sailingDate: string, returnDate: string): string {
  return `${cruiseLine}_${shipName}_${sailingDate}_${returnDate}`;
}

/** Legacy docId format (pre-migration) */
export function getLegacyItineraryDocId(cruiseLine: string, shipName: string, sailingDate: string): string {
  return `${cruiseLine}_${shipName}_${sailingDate}`;
}

/**
 * Compare CSV data with existing Firestore data to identify changes
 * @param csvData - CSV data to compare
 * @param onProgress - Optional callback for progress updates (current, total)
 */
export async function compareItineraryData(
  csvData: CsvItineraryRow[],
  onProgress?: (current: number, total: number) => void
): Promise<ComparisonResult> {
  const updatedFieldCounts: UpdatedFieldCounts = {
    departurePort: 0,
    itineraryTitle: 0,
    cruiseLength: 0,
    portNames: 0,
    portDates: 0,
    portTimes: 0,
    portsCount: 0
  };

  const result: ComparisonResult = {
    newItineraries: [],
    updatedItineraries: [],
    unchangedItineraries: [],
    summariesToUpdate: new Map(),
    oldDocIdsToDelete: [],
    updatedFieldCounts,
    itineraryChanges: new Map()
  };
  
  // Group CSV data by cruiseLine + shipName for summary generation
  const csvDataByShip = new Map<string, CsvItineraryRow[]>();
  
  for (const row of csvData) {
    const shipKey = `${row.cruiseLine}_${row.shipName}`;
    if (!csvDataByShip.has(shipKey)) {
      csvDataByShip.set(shipKey, []);
    }
    csvDataByShip.get(shipKey)!.push(row);
  }
  
  // Process each itinerary row
  debug.log(`[ItineraryComparator] Processing ${csvData.length} itinerary rows...`);
  for (let i = 0; i < csvData.length; i++) {
    const row = csvData[i];
    const newDocId = getItineraryDocId(row.cruiseLine, row.shipName, row.sailingDate, row.returnDate);
    const oldDocId = getLegacyItineraryDocId(row.cruiseLine, row.shipName, row.sailingDate);
    
    try {
      const newDocRef = doc(db, 'itineraries', newDocId);
      const existingNewDoc = await getDoc(newDocRef);
      
      if (existingNewDoc.exists()) {
        // New-format doc exists: compare and mark updated or unchanged
        const existingData = existingNewDoc.data() as Itinerary;
        const changedFields = getChangedFields(row, existingData);
        if (changedFields.length > 0) {
          result.updatedItineraries.push(row);
          for (const field of changedFields) {
            updatedFieldCounts[field]++;
          }
          const shouldNotify = changedFields.some(f => NOTIFICATION_TRIGGER_FIELDS.includes(f));
          const changeDescription = formatChangeDescription(changedFields, row, existingData);
          result.itineraryChanges.set(newDocId, {
            changedFields,
            shouldNotify,
            changeDescription
          });
        } else {
          result.unchangedItineraries.push(row);
        }
      } else {
        // New-format doc does not exist: check for legacy doc
        const oldDocRef = doc(db, 'itineraries', oldDocId);
        const existingOldDoc = await getDoc(oldDocRef);
        
        if (existingOldDoc.exists()) {
          const oldData = existingOldDoc.data() as Itinerary;
          if (oldData.returnDate === row.returnDate) {
            // Same itinerary: migrate to new format, schedule old for deletion
            result.updatedItineraries.push(row);
            result.oldDocIdsToDelete.push(oldDocId);
            const changedFields = getChangedFields(row, oldData);
            for (const field of changedFields) {
              updatedFieldCounts[field]++;
            }
            const shouldNotify = changedFields.some(f => NOTIFICATION_TRIGGER_FIELDS.includes(f));
            const changeDescription = formatChangeDescription(changedFields, row, oldData);
            result.itineraryChanges.set(newDocId, {
              changedFields,
              shouldNotify,
              changeDescription
            });
          } else {
            // Different itineraries (same ship/date, different length): migrate old doc to new format, add CSV as new
            const oldDocAsNewId = getItineraryDocId(oldData.cruiseLine, oldData.shipName, oldData.sailingDate, oldData.returnDate);
            result.updatedItineraries.push({
              cruiseLine: oldData.cruiseLine,
              shipName: oldData.shipName,
              sailingDate: oldData.sailingDate,
              returnDate: oldData.returnDate,
              departurePort: oldData.departurePort,
              portsOfCall: oldData.ports || [],
              itineraryTitle: oldData.itineraryTitle || '',
              cruiseLength: oldData.cruiseLength || 0,
              shipLocation: row.shipLocation,
              shipUrl: row.shipUrl,
              shipDeckplan: row.shipDeckplan
            } as CsvItineraryRow);
            result.itineraryChanges.set(oldDocAsNewId, {
              changedFields: [],
              shouldNotify: false,
              changeDescription: ''
            });
            result.newItineraries.push(row);
            result.oldDocIdsToDelete.push(oldDocId);
          }
        } else {
          result.newItineraries.push(row);
        }
      }
      
      // Log progress every 10 rows and call progress callback
      if ((i + 1) % 10 === 0 || i === csvData.length - 1) {
        debug.log(`[ItineraryComparator] Processed ${i + 1}/${csvData.length} rows...`);
        onProgress?.(i + 1, csvData.length);
      }
    } catch (error) {
      debug.warn(`[ItineraryComparator] Error reading itinerary ${newDocId}:`, error);
      result.newItineraries.push(row);
    }
  }
  debug.log(`[ItineraryComparator] Finished processing ${csvData.length} rows`);
  onProgress?.(csvData.length, csvData.length);
  
  // Generate summaries for each ship
  for (const [shipKey, rows] of csvDataByShip.entries()) {
    const [cruiseLine, shipName] = shipKey.split('_', 2);
    
    // Get existing summary if it exists
    const summaryRef = doc(db, 'itinerarySummaries', shipKey);
    let existingSummary: ItinerarySummary | null = null;
    
    try {
      const summaryDoc = await getDoc(summaryRef);
      if (summaryDoc.exists()) {
        existingSummary = summaryDoc.data() as ItinerarySummary;
      }
    } catch (error) {
      debug.warn(`Error reading summary ${shipKey}:`, error);
    }
    
    // Collect composite keys (sailingDate_returnDate) from CSV data
    const compositeKeys = new Set<string>();
    const itineraryDetails: { [compositeKey: string]: { cruiseLength: number; itineraryTitle: string; returnDate?: string } } = {};
    
    // Add composite keys and details from CSV
    rows.forEach(row => {
      const compositeKey = `${row.sailingDate}_${row.returnDate}`;
      compositeKeys.add(compositeKey);
      itineraryDetails[compositeKey] = {
        cruiseLength: row.cruiseLength || 0,
        itineraryTitle: row.itineraryTitle || '',
        returnDate: row.returnDate
      };
    });
    
    // Add existing keys and details if summary exists (support both old and new format)
    if (existingSummary?.departureDates) {
      for (const key of existingSummary.departureDates) {
        compositeKeys.add(key);
        if (existingSummary.itineraryDetails?.[key] && !itineraryDetails[key]) {
          itineraryDetails[key] = existingSummary.itineraryDetails[key];
        }
      }
    }
    
    // Migrate old-format keys and fetch missing details from Firestore
    for (const key of Array.from(compositeKeys)) {
      const isOldFormat = !key.includes('_') || key.split('_').length < 2;
      try {
        if (isOldFormat) {
          // Old format: single sailingDate key - fetch doc and migrate to composite key
          const oldDocId = getLegacyItineraryDocId(cruiseLine, shipName, key);
          const itineraryDoc = await getDoc(doc(db, 'itineraries', oldDocId));
          if (itineraryDoc.exists()) {
            const itineraryData = itineraryDoc.data() as Itinerary;
            const newKey = `${itineraryData.sailingDate}_${itineraryData.returnDate}`;
            compositeKeys.add(newKey);
            compositeKeys.delete(key);
            itineraryDetails[newKey] = {
              cruiseLength: itineraryData.cruiseLength || 0,
              itineraryTitle: itineraryData.itineraryTitle || '',
              returnDate: itineraryData.returnDate
            };
            delete itineraryDetails[key];
          }
        } else if (!itineraryDetails[key]) {
          // New format: fetch if details missing
          const [sailingDate, returnDate] = key.split('_', 2);
          const newDocId = getItineraryDocId(cruiseLine, shipName, sailingDate, returnDate);
          const itineraryDoc = await getDoc(doc(db, 'itineraries', newDocId));
          if (itineraryDoc.exists()) {
            const itineraryData = itineraryDoc.data() as Itinerary;
            itineraryDetails[key] = {
              cruiseLength: itineraryData.cruiseLength || 0,
              itineraryTitle: itineraryData.itineraryTitle || '',
              returnDate: itineraryData.returnDate
            };
          }
        }
      } catch (error) {
        debug.warn(`Error fetching itinerary details for ${key}:`, error);
      }
    }
    
    // Sort composite keys (by sailing date, then return date)
    const sortedDates = Array.from(compositeKeys).sort();
    
    // Create or update summary
    const summary: ItinerarySummary = {
      cruiseLine,
      shipName,
      departureDates: sortedDates,
      itineraryDetails: Object.keys(itineraryDetails).length > 0 ? itineraryDetails : undefined,
      lastUpdated: new Date()
    };
    
    result.summariesToUpdate.set(shipKey, summary);
  }
  
  return result;
}

export function formatChangeDescription(
  changedFields: Array<keyof UpdatedFieldCounts>,
  newRow: CsvItineraryRow,
  existing?: Itinerary
): string {
  const parts: string[] = [];

  if (changedFields.includes('cruiseLength') && newRow.cruiseLength) {
    if (existing?.cruiseLength && existing.cruiseLength !== newRow.cruiseLength) {
      parts.push(`Cruise length updated to ${newRow.cruiseLength} days (was ${existing.cruiseLength})`);
    } else {
      parts.push(`Cruise length updated to ${newRow.cruiseLength} days`);
    }
  }

  if (changedFields.includes('departurePort') && newRow.departurePort) {
    parts.push(`Departure port changed to ${newRow.departurePort}`);
  }

  if (changedFields.includes('portsCount')) {
    const newCount = newRow.portsOfCall?.length || 0;
    const oldCount = existing?.ports?.length || 0;
    const diff = newCount - oldCount;
    if (diff > 0) {
      parts.push(`${diff} port stop${diff > 1 ? 's' : ''} added`);
    } else if (diff < 0) {
      parts.push(`${Math.abs(diff)} port stop${Math.abs(diff) > 1 ? 's' : ''} removed`);
    } else {
      parts.push('Port stops modified');
    }
  }

  if (changedFields.includes('portNames') && !changedFields.includes('portsCount')) {
    parts.push('Port locations updated');
  }

  if (changedFields.includes('portDates')) {
    parts.push('Port dates adjusted');
  }

  if (changedFields.includes('portTimes')) {
    parts.push('Port arrival/departure times adjusted');
  }

  return parts.join('; ');
}

export function getChangedFields(csvRow: CsvItineraryRow, existing: Itinerary): Array<keyof UpdatedFieldCounts> {
  const changes = new Set<keyof UpdatedFieldCounts>();

  if (csvRow.departurePort !== existing.departurePort) {
    changes.add('departurePort');
  }
  if (csvRow.itineraryTitle !== existing.itineraryTitle) {
    changes.add('itineraryTitle');
  }
  if (csvRow.cruiseLength !== existing.cruiseLength) {
    changes.add('cruiseLength');
  }

  const csvPorts = csvRow.portsOfCall || [];
  const existingPorts = existing.ports || [];

  if (csvPorts.length !== existingPorts.length) {
    changes.add('portsCount');
  }

  const minLen = Math.min(csvPorts.length, existingPorts.length);
  for (let i = 0; i < minLen; i++) {
    const csvPort = csvPorts[i];
    const existingPort = existingPorts[i];

    if (csvPort.name !== existingPort.name) {
      changes.add('portNames');
    }
    if (csvPort.date !== existingPort.date) {
      changes.add('portDates');
    }
    if (
      (csvPort.arrivalTime || '') !== (existingPort.arrivalTime || '') ||
      (csvPort.departureTime || '') !== (existingPort.departureTime || '')
    ) {
      changes.add('portTimes');
    }
  }

  return Array.from(changes);
}

/**
 * Check if a CSV row has changes compared to existing Firestore data
 */
export function hasChanges(csvRow: CsvItineraryRow, existing: Itinerary): boolean {
  return getChangedFields(csvRow, existing).length > 0;
}

/**
 * Generate update batches for Firestore writes
 * Returns arrays of documents to create/update, grouped for batch operations
 */
export function generateUpdateBatches(
  comparisonResult: ComparisonResult
): {
  itineraryBatches: Array<{ docId: string; data: Itinerary }>;
  summaryBatches: Array<{ docId: string; data: ItinerarySummary }>;
  oldDocIdsToDelete: string[];
} {
  const itineraryBatches: Array<{ docId: string; data: Itinerary }> = [];
  const summaryBatches: Array<{ docId: string; data: ItinerarySummary }> = [];
  
  // Process new and updated itineraries (use new docId format)
  const itinerariesToProcess = [
    ...comparisonResult.newItineraries,
    ...comparisonResult.updatedItineraries
  ];
  
  for (const row of itinerariesToProcess) {
    const docId = getItineraryDocId(row.cruiseLine, row.shipName, row.sailingDate, row.returnDate);
    
    // Build itinerary object, filtering out undefined values
    const itineraryData: any = {
      cruiseLine: row.cruiseLine,
      shipName: row.shipName,
      sailingDate: row.sailingDate,
      returnDate: row.returnDate,
      departurePort: row.departurePort,
      ports: row.portsOfCall || [],
      itineraryTitle: row.itineraryTitle || '',
      cruiseLength: row.cruiseLength || 0,
      lastUpdated: new Date()
    };
    
    // Remove undefined values to avoid Firestore errors
    const itinerary: Itinerary = Object.fromEntries(
      Object.entries(itineraryData).filter(([, value]) => value !== undefined)
    ) as unknown as Itinerary;
    
    itineraryBatches.push({ docId, data: itinerary });
  }
  
  // Process summaries
  for (const [shipKey, summary] of comparisonResult.summariesToUpdate.entries()) {
    summaryBatches.push({ docId: shipKey, data: summary });
  }
  
  return {
    itineraryBatches,
    summaryBatches,
    oldDocIdsToDelete: comparisonResult.oldDocIdsToDelete
  };
}

